import React from 'react';
import { api } from '../services/api';
import { useApiData } from '../hooks/useApiData';
import { SeriesBars, MetricBar, ScoreCell, LoadingState, ErrorState } from './primitives';
import { rangeLabel } from './RangeControl';
import { fmtInt } from '../lib/format';
import { RangeRep, SeriesField, SeriesPoint, TimeRange } from '../types/superadmin';

/** The rep's range scores shown as summary stats in the expanded panel. */
const DIMENSIONS: Array<{ key: keyof RangeRep; label: string; bar?: boolean }> = [
  { key: 'objFaqScore', label: 'Obj/FAQ' },
  { key: 'landmineScore', label: 'Landmine' },
  { key: 'competitorScore', label: 'Competitor' },
  { key: 'playbookScore', label: 'Playbook' },
  { key: 'callQualityScore', label: 'Call quality', bar: true },
  { key: 'trainingQualityScore', label: 'Training quality', bar: true },
  { key: 'trainingCompletion', label: 'Training completion', bar: true }
];

// All by-day charts shown in the accordion. Count charts auto-scale; score
// charts are fixed to 0-100 and render '%'. Same date format as each other.
const CHARTS: Array<{ field: SeriesField; title: string; unit?: string; score?: boolean }> = [
  { field: 'minutes', title: 'Minutes by day', unit: ' min' },
  { field: 'sessions', title: 'Sessions by day' },
  { field: 'objFaqScore', title: 'Obj/FAQ by day', unit: '%', score: true },
  { field: 'landmineScore', title: 'Landmine by day', unit: '%', score: true },
  { field: 'competitorScore', title: 'Competitor by day', unit: '%', score: true },
  { field: 'playbookScore', title: 'Playbook by day', unit: '%', score: true },
  { field: 'callQualityScore', title: 'Call quality by day', unit: '%', score: true },
  { field: 'trainingQualityScore', title: 'Training quality by day', unit: '%', score: true },
  { field: 'trainingCompletion', title: 'Training completion by day', unit: '%', score: true }
];

function ChartBlock({
  data,
  chart,
  range,
  repName
}: {
  data: SeriesPoint[];
  chart: { field: SeriesField; title: string; unit?: string; score?: boolean };
  range: TimeRange;
  repName: string;
}) {
  return (
    <div className="chart-block">
      <div className="section-head">
        <div>
          <h3>{chart.title}</h3>
          <p className="desc">
            {repName} · {rangeLabel(range)}.
          </p>
        </div>
      </div>
      <SeriesBars data={data} field={chart.field} unit={chart.unit} maxValue={chart.score ? 100 : undefined} />
    </div>
  );
}

/**
 * Expanded per-rep panel: the rep's five dimension scores for the range, then
 * one by-day chart per metric (Minutes, Sessions, and each score), all in the
 * same date format and gap-filled across the range. Series is lazy-loaded when
 * the row is expanded.
 */
export default function RepSeriesPanel({ rep, range }: { rep: RangeRep; range: TimeRange }) {
  const { data, loading, error } = useApiData(
    () => api.getRepSeries(rep.tenantId, rep.userId, range),
    [rep.tenantId, rep.userId, JSON.stringify(range)]
  );

  const series = data?.data.series ?? [];

  return (
    <div className="rep-panel">
      <div className="rep-panel-scores">
        {DIMENSIONS.map(({ key, label, bar }) => {
          const value = rep[key] as number | null;
          return (
            <div className="rep-panel-score" key={String(key)}>
              <div className="lbl">{label}</div>
              {bar ? <MetricBar value={value} /> : <ScoreCell value={value} />}
            </div>
          );
        })}
        <div className="rep-panel-score">
          <div className="lbl">Training sessions</div>
          <span className="metric-num">{fmtInt(rep.trainingSessions)}</span>
        </div>
      </div>

      {loading && <LoadingState label="Loading rep activity…" />}
      {error && <ErrorState message={error} />}
      {!loading && !error && (
        <div className="chart-grid">
          {CHARTS.map((chart) => (
            <ChartBlock key={chart.field} data={series} chart={chart} range={range} repName={rep.name} />
          ))}
        </div>
      )}
    </div>
  );
}
